import { useState, useEffect } from 'react';
import { X, Calendar, User, PackageCheck, ArrowRight } from 'lucide-react';
import { format, addDays } from 'date-fns';
import toast from 'react-hot-toast';
import { useJobStore, type Job } from '../lib/store';
import { api } from '../lib/api';

interface Props {
  job: Job;
  open: boolean;
  onClose: () => void;
  onBooked?: () => void;
}

const ASSIGNEES = ['sammy', 'jim', 'stewart'];

/**
 * Modal that Sammy uses to book a follow-up visit for a job whose
 * ordered parts have just arrived. Picks a date + assignee, then flips
 * the job status from "Follow-up Visit Required" -> "Pending" on the new date.
 */
export default function BookFollowUpModal({ job, open, onClose, onBooked }: Props) {
  const { updateJob } = useJobStore();
  const [date, setDate] = useState(format(addDays(new Date(), 1), 'yyyy-MM-dd'));
  const [time, setTime] = useState<string>(job.time || '09:00');
  const [assignedTo, setAssignedTo] = useState<string>(job.assigned_to || 'jim');
  const [saving, setSaving] = useState(false);
  const [receivedOrders, setReceivedOrders] = useState<any[]>([]);

  useEffect(() => {
    if (!open || !job?.id) return;
    // Load parts orders for this job so we can show what came in
    (async () => {
      try {
        const res = await api.get('/parts-orders', { params: { job_id: job.id } });
        const orders = (res.data || []).filter((o: any) => o.status === 'Received');
        setReceivedOrders(orders);
      } catch {
        setReceivedOrders([]);
      }
    })();
    // Reset date to tomorrow every time modal opens
    setDate(format(addDays(new Date(), 1), 'yyyy-MM-dd'));
    setTime(job.time || '09:00');
    setAssignedTo(job.assigned_to || 'jim');
  }, [open, job?.id, job.time, job.assigned_to]);

  if (!open) return null;

  const submit = async () => {
    if (!date) {
      toast.error('Pick a date for the follow-up visit');
      return;
    }
    setSaving(true);
    const updated = await updateJob(job.id, {
      date,
      time: time || undefined,
      assigned_to: assignedTo,
      status: 'Pending',
    });
    setSaving(false);
    if (updated) {
      toast.success(`Follow-up booked for ${format(new Date(date), 'EEE d MMM')}`);
      onBooked?.();
      onClose();
    } else {
      toast.error('Could not book follow-up. Try again.');
    }
  };

  const partsSummary = receivedOrders.flatMap((o) =>
    (o.items || []).map((it: any) => `${it.quantity}× ${it.name}`)
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/50 backdrop-blur-sm p-0 sm:p-4 animate-fade-in"
      onClick={onClose}
      data-testid="book-followup-modal"
    >
      <div
        className="card w-full sm:max-w-lg animate-scale-in bg-white shadow-pop rounded-t-2xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-slate-100">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 grid place-items-center shrink-0">
              <PackageCheck className="h-5 w-5 text-amber-700" />
            </div>
            <div>
              <h3 className="font-display font-bold text-lg text-slate-900 leading-tight">
                Book follow-up visit
              </h3>
              <p className="text-sm text-slate-500 mt-0.5">
                Parts for <span className="font-semibold text-slate-700">{job.client_name || job.title}</span> have arrived
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"
            data-testid="book-followup-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Parts summary */}
        {partsSummary.length > 0 && (
          <div className="px-5 py-3 bg-emerald-50/70 border-b border-emerald-100">
            <div className="label-caps text-emerald-700 mb-1">Parts in stock</div>
            <div className="text-sm text-slate-700 leading-relaxed">
              {partsSummary.slice(0, 6).join(' · ')}
              {partsSummary.length > 6 && ` · +${partsSummary.length - 6} more`}
            </div>
          </div>
        )}

        {/* Form */}
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-caps mb-1.5 block flex items-center gap-1.5">
                <Calendar className="h-3 w-3" /> Visit date
              </label>
              <input
                type="date"
                value={date}
                min={format(new Date(), 'yyyy-MM-dd')}
                onChange={(e) => setDate(e.target.value)}
                className="input"
                data-testid="book-followup-date"
              />
            </div>
            <div>
              <label className="label-caps mb-1.5 block">Time</label>
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="input"
                data-testid="book-followup-time"
              />
            </div>
          </div>

          <div>
            <label className="label-caps mb-1.5 block flex items-center gap-1.5">
              <User className="h-3 w-3" /> Assign to
            </label>
            <div className="grid grid-cols-3 gap-2">
              {ASSIGNEES.map((name) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => setAssignedTo(name)}
                  className={`px-3 py-2.5 rounded-md text-sm font-semibold transition capitalize ${
                    assignedTo === name
                      ? 'bg-brand-600 text-white shadow-sm'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                  data-testid={`book-followup-assignee-${name}`}
                >
                  {name}
                </button>
              ))}
            </div>
          </div>

          {/* Reminder about job context */}
          <div className="text-xs text-slate-500 bg-slate-50 rounded-md px-3 py-2 border border-slate-100">
            <span className="font-semibold text-slate-700">Note:</span> {job.job_number || job.title} at{' '}
            {job.client_address || job.location?.address || 'no address'} will be re-scheduled and its status will move
            back to <span className="chip bg-amber-100 text-amber-800 ring-1 ring-amber-200 !py-0 !px-1.5 !text-[10px]">Pending</span>.
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 p-4 border-t border-slate-100 bg-slate-50/40 rounded-b-2xl">
          <button
            onClick={onClose}
            className="btn btn-secondary"
            data-testid="book-followup-cancel"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="btn btn-primary"
            data-testid="book-followup-confirm"
          >
            {saving ? 'Booking…' : (
              <>
                Book follow-up <ArrowRight className="h-4 w-4" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
